import { useSyncExternalStore } from "react";

/**
 * Application web installable : enregistrement du service worker, invitation d'installation
 * (navigateurs Chromium) et état de la connexion. Voir pwa/sw.js pour ce qui est mis en cache.
 */

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export type InstallPlatform = "ios" | "android" | "desktop-chromium" | "desktop-safari" | "desktop-firefox" | "other";

let deferredPrompt: InstallPromptEvent | null = null;
let installedNow = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => listeners.delete(l); };

/** À appeler avant le premier rendu : l'invitation d'installation peut arriver très tôt. */
export function initPwa(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e as InstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installedNow = true;
    emit();
  });
  if (import.meta.env.PROD && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch(() => { /* le site reste utilisable sans service worker */ });
    });
  }
}

/** Plateforme, pour expliquer comment ajouter l'application à l'écran d'accueil. */
export function installPlatform(userAgent: string, maxTouchPoints = 0): InstallPlatform {
  const ua = userAgent;
  const ipadOs = /Macintosh/.test(ua) && maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || ipadOs) return "ios";
  if (/Android/.test(ua)) return "android";
  if (/Firefox\//.test(ua)) return "desktop-firefox";
  if (/Edg\/|Chrome\/|Chromium\//.test(ua)) return "desktop-chromium";
  if (/Safari\//.test(ua) && /Macintosh/.test(ua)) return "desktop-safari";
  return "other";
}

/** Déjà ouverte comme application (écran d'accueil, Dock, fenêtre dédiée) ? */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function useInstall() {
  const canPrompt = useSyncExternalStore(subscribe, () => deferredPrompt !== null, () => false);
  const installed = useSyncExternalStore(subscribe, () => installedNow || isStandalone(), () => false);
  const platform = typeof navigator === "undefined" ? "other" : installPlatform(navigator.userAgent, navigator.maxTouchPoints);
  /** Ouvre l'invitation du navigateur ; renvoie false si elle n'est pas disponible (instructions à afficher). */
  const prompt = async (): Promise<boolean> => {
    const event = deferredPrompt;
    if (!event) return false;
    await event.prompt();
    await event.userChoice;
    deferredPrompt = null;
    emit();
    return true;
  };
  return { canPrompt, installed, platform, prompt };
}

const subscribeOnline = (l: () => void) => {
  window.addEventListener("online", l);
  window.addEventListener("offline", l);
  return () => { window.removeEventListener("online", l); window.removeEventListener("offline", l); };
};

export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}
